import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ExternalLink, ImageOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  getSafeMediaDownloadUrl,
  getSafeMediaUrl,
  getSafeNavigationUrl,
} from '../utils/safeUrl.ts';

interface ImagePreviewModalProps {
  imageUrl: string | null;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  adId?: number;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  imageUrl,
  isOpen,
  onClose,
  title,
  adId,
}) => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const safeImageUrl = useMemo(() => getSafeMediaUrl(imageUrl, 'image'), [imageUrl]);
  const safeDownloadUrl = useMemo(
    () => getSafeMediaDownloadUrl(imageUrl, 'image'),
    [imageUrl],
  );
  const safeNavigationUrl = useMemo(() => getSafeNavigationUrl(imageUrl), [imageUrl]);

  useEffect(() => {
    setLoading(Boolean(safeImageUrl));
    setErrored(Boolean(imageUrl) && !safeImageUrl);
  }, [imageUrl, safeImageUrl]);

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      return;
    }
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const downloadName = adId ? `anuncio-${adId}.jpg` : 'anuncio.jpg';

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      <div
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      <div
        className={`relative z-10 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl transition-all duration-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} ${isDark ? 'bg-gray-800' : 'bg-white'}`}
      >
        <div className={`sticky top-0 flex items-center justify-between gap-3 border-b px-5 py-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="min-w-0">
            <h3 className={`truncate text-base font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {title || 'Visualização do Anúncio'}
            </h3>
            {adId && (
              <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                ID interno: {adId}
              </p>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {!errored && (safeDownloadUrl || safeNavigationUrl) && (
              <>
                {safeDownloadUrl && (
                  <a
                    href={safeDownloadUrl}
                    download={downloadName}
                    title="Baixar criativo"
                    className={`rounded-xl p-2 transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                  >
                    <Download className="h-5 w-5" />
                  </a>
                )}
                {safeNavigationUrl && (
                  <button
                    type="button"
                    onClick={() => window.open(safeNavigationUrl, '_blank', 'noopener,noreferrer')}
                    title="Abrir em nova aba"
                    className={`rounded-xl p-2 transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                  >
                    <ExternalLink className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              title="Fechar (ESC)"
              className={`rounded-xl p-2 transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className={`flex min-h-[320px] items-center justify-center overflow-auto p-5 ${isDark ? 'bg-gray-900/30' : 'bg-gray-50'}`} style={{ maxHeight: 'calc(90vh - 73px)' }}>
          {loading && safeImageUrl && !errored && (
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin mx-auto" />
              <p className={`mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Carregando criativo...
              </p>
            </div>
          )}

          {safeImageUrl && !errored && (
            <img
              src={safeImageUrl}
              alt={title || 'Criativo do anúncio'}
              className={`max-h-[70vh] max-w-full rounded-lg object-contain ${loading ? 'hidden' : 'block'}`}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setErrored(true);
              }}
            />
          )}

          {(!safeImageUrl || errored) && !loading && (
            <div className="text-center py-8">
              <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <ImageOff className={`w-8 h-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Não foi possível carregar a imagem.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImagePreviewModal;
